import { IconBrandWhatsapp, IconMail, IconMessageCircle, IconPhone, IconSend, IconSparkles } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, type ReplyVia, type ThreadMessage } from "../api";
import { Empty, ErrorNote, Spinner } from "../components/ui";
import { formatDateTime } from "../format";
import type { Key } from "../i18n";
import { useSession } from "../session";
import { clearUnread } from "../unread";

const POLL_MS = 10_000;

/** The thread across channels (docs/16 §2.4): chat style, newest last, the assistant and the consultant distinguished. */
export function ConversationPage() {
  const { me, t } = useSession();
  const [messages, setMessages] = useState<ThreadMessage[] | undefined>(undefined);
  const [replyVia, setReplyVia] = useState<ReplyVia>("email");
  const [error, setError] = useState<{ message: string; signIn?: boolean } | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [handedOver, setHandedOver] = useState(false);
  const [tick, setTick] = useState(0);
  const viaChosen = useRef(false);

  const load = useCallback(async () => {
    const res = await api.conversation();
    setMessages(res.messages);
    clearUnread();
    if (!viaChosen.current) setReplyVia(res.replyVia);
    setError(null);
  }, []);

  useEffect(() => {
    load().catch((err: unknown) => {
      if (err instanceof ApiError && err.code === "sign_in_required") setError({ message: t("linkSessionNote"), signIn: true });
      else setError({ message: t("somethingWrong") });
    });
  }, [load, tick, t]);

  // The consultant may answer from the CRM: poll while the page is visible.
  useEffect(() => {
    if (error) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load().catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load, error]);

  // Chat style: the newest message sits just above the composer.
  const count = messages?.length ?? 0;
  useEffect(() => {
    if (count > 0) window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [count]);

  if (!me) return null;

  const consultantName = me.consultant?.name ?? t("yourConsultantLower");

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await api.sendMessage({ text: body, replyVia });
      setMessages((prev) => [...(prev ?? []), res.message, ...(res.assistant ? [res.assistant] : [])]);
      setHandedOver(res.handedOver);
      setText("");
    } catch {
      setSendError(t("somethingWrong"));
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <main className="page">
        <h1>{t("conversation")}</h1>
        <ErrorNote onRetry={error.signIn ? undefined : () => setTick((n) => n + 1)}>{error.message}</ErrorNote>
        {error.signIn && (
          <a href="/signin" className="button button-primary">
            {t("signIn")}
          </a>
        )}
      </main>
    );
  }

  return (
    <main className="page page-conversation">
      <header className="conversation-head">
        <h1>{t("conversation")}</h1>
        <p className="muted small">{t("conversationLead", { name: consultantName })}</p>
      </header>

      {messages === undefined ? (
        <Spinner />
      ) : messages.length === 0 ? (
        <Empty title={t("conversationEmpty", { name: consultantName })} />
      ) : (
        <ol className="thread" aria-live="polite">
          {messages.map((m) => (
            <Bubble key={m.id} message={m} />
          ))}
        </ol>
      )}
      {handedOver && (
        <p className="notice small">
          <IconSparkles size={16} /> {t("consultantTold")}
        </p>
      )}
      <form className="composer" onSubmit={(e) => void send(e)}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("writePlaceholder")} rows={2} maxLength={8000} aria-label={t("writePlaceholder")} />
        {sendError && <ErrorNote>{sendError}</ErrorNote>}
        <div className="composer-row">
          <label className="composer-via">
            <span className="muted small">{t("replyMeOn")}</span>
            <select
              value={replyVia}
              onChange={(e) => {
                viaChosen.current = true;
                setReplyVia(e.target.value as ReplyVia);
              }}
            >
              <option value="area">{t("replyHere")}</option>
              <option value="email">{t("email")}</option>
              <option value="whatsapp">{t("whatsapp")}</option>
            </select>
          </label>
          <button type="submit" className="button button-primary button-small" disabled={sending || !text.trim()}>
            <IconSend size={16} /> {sending ? t("sending") : t("send")}
          </button>
        </div>
      </form>
    </main>
  );
}

const KIND_KEYS: Record<string, Key> = {
  change_request: "kindChangeRequest",
  cancellation_request: "kindCancellationRequest",
  question: "kindQuestion",
  handoff: "kindHandoff",
};

export function channelChip(channel: string, t: (key: Key) => string): ReactNode {
  switch (channel) {
    case "email":
      return (
        <span className="chip chip-channel">
          <IconMail size={12} /> {t("email")}
        </span>
      );
    case "whatsapp":
      return (
        <span className="chip chip-channel">
          <IconBrandWhatsapp size={12} /> {t("whatsapp")}
        </span>
      );
    case "call":
      return (
        <span className="chip chip-channel">
          <IconPhone size={12} /> {t("call")}
        </span>
      );
    case "area":
      return (
        <span className="chip chip-channel">
          <IconMessageCircle size={12} /> {t("channelHere")}
        </span>
      );
    default:
      return null;
  }
}

function Bubble({ message: m }: { message: ThreadMessage }) {
  const { lang, t } = useSession();
  const side = m.author === "customer" ? "right" : "left";
  const kindKey = KIND_KEYS[m.kind];
  const label = m.author === "assistant" ? t("assistant") : m.author === "consultant" ? (m.authorName ?? t("yourConsultant")) : m.author === "system" ? (m.authorName ?? "") : null;
  return (
    <li className={`bubble-row bubble-${side}`}>
      <div className={`bubble bubble-${m.author}`}>
        {label && (
          <span className="bubble-label">
            {m.author === "assistant" && <IconSparkles size={12} />} {label}
          </span>
        )}
        {m.subject && m.kind === "message" && <strong className="bubble-subject">{m.subject}</strong>}
        {kindKey && <span className="chip chip-kind">{t(kindKey)}</span>}
        <p className="bubble-body">{m.body}</p>
        <span className="bubble-meta">
          {channelChip(m.channel, t)}
          <time dateTime={m.at}>{formatDateTime(m.at, lang)}</time>
        </span>
      </div>
    </li>
  );
}
